/**
 * CrewForm Component
 *
 * Form for creating and editing crew members.
 * Handles validation, submission, and error display.
 */

import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate } from 'react-router-dom';
import {
  createUser,
  updateUser,
  getUser,
  type User,
} from '../../api/users';
import { getErrorMessage } from '../../api/client';
import { useOrganization } from '../../context/OrganizationContext';
import { useAuth } from '../../context/AuthContext';
import FormField from '../ui/FormField';
import Button from '../ui/Button';

// =============================================================================
// Validation Schema
// =============================================================================

const crewSchema = z.object({
  email: z.string().min(1, 'Email is required').email('Invalid email address'),
  firstName: z.string().min(1, 'First name is required').max(100, 'First name too long'),
  lastName: z.string().min(1, 'Last name is required').max(100, 'Last name too long'),
  phone: z.string().max(20, 'Phone number too long').optional().nullable(),
  role: z.enum(['OWNER', 'ADMIN', 'CREW']).default('CREW'),
  // Payment tracking - rates stored as dollars in form, converted to cents for API
  defaultHourlyRate: z.string().optional().nullable(),
  defaultDailyRate: z.string().optional().nullable(),
});

type CrewFormData = z.infer<typeof crewSchema>;

// =============================================================================
// Currency Helpers
// =============================================================================

/** Convert cents to dollars string for display */
function centsToDollars(cents: number | null | undefined): string {
  if (cents === null || cents === undefined) return '';
  return (cents / 100).toFixed(2);
}

/** Convert dollars string to cents for API */
function dollarsToCents(dollars: string | null | undefined): number | null {
  if (!dollars || dollars.trim() === '') return null;
  const parsed = parseFloat(dollars);
  if (isNaN(parsed)) return null;
  return Math.round(parsed * 100);
}

// =============================================================================
// CrewForm Component
// =============================================================================

interface CrewFormProps {
  userId?: string;
  onSuccess?: (user: User) => void;
}

interface CreatedUserInfo {
  user: User;
  generatedPassword: string;
}

export default function CrewForm({ userId, onSuccess }: CrewFormProps) {
  const navigate = useNavigate();
  const { paymentTrackingEnabled } = useOrganization();
  const { user: currentUser } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(!!userId);
  const [createdUser, setCreatedUser] = useState<CreatedUserInfo | null>(null);
  const [copied, setCopied] = useState(false);

  const isEditing = !!userId;
  const showRates = isEditing && paymentTrackingEnabled;
  // Editing your own record: role is locked. Self-demotion is refused by the
  // server too, so offering it would only produce a confusing error.
  const isSelf = isEditing && !!currentUser && currentUser.id === userId;

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CrewFormData>({
    resolver: zodResolver(crewSchema),
    defaultValues: {
      role: 'CREW',
    },
  });

  // Load existing user data if editing
  useEffect(() => {
    if (!userId) return;

    const loadUser = async () => {
      try {
        const { user } = await getUser(userId);
        reset({
          email: user.email,
          firstName: user.firstName,
          lastName: user.lastName,
          phone: user.phone,
          role: user.role,
          defaultHourlyRate: centsToDollars(user.defaultHourlyRate),
          defaultDailyRate: centsToDollars(user.defaultDailyRate),
        });
      } catch (err) {
        setError(getErrorMessage(err));
      } finally {
        setIsLoading(false);
      }
    };

    loadUser();
  }, [userId, reset]);

  const onSubmit = async (data: CrewFormData) => {
    setError(null);
    setIsSubmitting(true);

    try {
      // Build base payload
      const basePayload = {
        email: data.email,
        firstName: data.firstName,
        lastName: data.lastName,
        phone: data.phone || null,
        role: data.role,
      };

      let result: User;

      if (isEditing) {
        // Include rate fields when editing (converted to cents)
        const updatePayload = {
          ...basePayload,
          ...(showRates && {
            defaultHourlyRate: dollarsToCents(data.defaultHourlyRate),
            defaultDailyRate: dollarsToCents(data.defaultDailyRate),
          }),
        };
        const response = await updateUser(userId, updatePayload);
        result = response.user;
      } else {
        // When creating, don't send rates — they come from the org defaults
        const response = await createUser(basePayload);
        result = response.user;

        // Show the generated password to the admin
        if (response.generatedPassword) {
          setCreatedUser({ user: result, generatedPassword: response.generatedPassword });
          return;
        }
      }

      if (onSuccess) {
        onSuccess(result);
      } else {
        navigate(`/admin/crew/${result.id}`);
      }
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCopyPassword = async () => {
    if (!createdUser) return;
    try {
      await navigator.clipboard.writeText(createdUser.generatedPassword);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for non-HTTPS or older browsers
      const textArea = document.createElement('textarea');
      textArea.value = createdUser.generatedPassword;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-gray-200 rounded w-1/4"></div>
          <div className="grid grid-cols-2 gap-6">
            <div className="h-10 bg-gray-200 rounded"></div>
            <div className="h-10 bg-gray-200 rounded"></div>
          </div>
          <div className="h-10 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  // Show generated password after successful creation
  if (createdUser) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="text-center mb-6">
          <div className="mx-auto flex items-center justify-center h-12 w-12 bg-green-100 mb-4">
            <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900">User Created Successfully</h2>
          <p className="text-gray-600 mt-1">
            {createdUser.user.firstName} {createdUser.user.lastName} ({createdUser.user.email})
          </p>
        </div>

        <div className="bg-green-50 border border-green-200 rounded-md p-4 mb-6">
          <div className="flex items-start">
            <svg className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            <div>
              <p className="text-sm font-medium text-green-800">Invitation email sent</p>
              <p className="text-sm text-green-700 mt-1">
                An invitation email has been sent to the user with a link to set up their account. The password below is their current temporary password, just in case.
              </p>
            </div>
          </div>
        </div>

        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">Backup Password</label>
          <div className="flex items-center gap-2">
            <code className="flex-1 block px-4 py-3 bg-gray-50 border border-gray-300 rounded-md font-mono text-lg tracking-wider select-all">
              {createdUser.generatedPassword}
            </code>
            <Button
              type="button"
              variant={copied ? 'primary' : 'secondary'}
              onClick={handleCopyPassword}
            >
              {copied ? 'Copied!' : 'Copy'}
            </Button>
          </div>
        </div>

        <div className="flex items-center justify-end gap-4 pt-6 border-t border-gray-200">
          <Button
            type="button"
            variant="secondary"
            onClick={() => navigate('/admin/crew')}
          >
            Back to Crew List
          </Button>
          <Button
            type="button"
            variant="primary"
            onClick={() => navigate(`/admin/crew/${createdUser.user.id}`)}
          >
            View User Profile
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <h2 className="text-xl font-semibold text-gray-900 mb-6">
        {isEditing ? 'Edit Crew Member' : 'Invite New Crew Member'}
      </h2>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">
            {error}
          </div>
        )}

        {/* Name Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* First Name */}
          <FormField
            label="First Name"
            {...register('firstName')}
            error={errors.firstName}
            required
            placeholder="John"
          />

          {/* Last Name */}
          <FormField
            label="Last Name"
            {...register('lastName')}
            error={errors.lastName}
            required
            placeholder="Doe"
          />
        </div>

        {/* Contact Info */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Email */}
          <FormField
            label="Email Address"
            type="email"
            {...register('email')}
            error={errors.email}
            required
            placeholder="john.doe@example.com"
          />

          {/* Phone */}
          <FormField
            label="Phone Number"
            type="tel"
            {...register('phone')}
            error={errors.phone}
            placeholder="(555) 123-4567"
          />
        </div>

        {/* Role & Status */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Role */}
          <div>
            <label htmlFor="role" className="block text-sm font-medium text-gray-700 mb-1">
              Role
            </label>
            <select
              id="role"
              {...register('role')}
              disabled={isSelf}
              className="block w-full pl-3 pr-10 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-rack focus:border-rack bg-white disabled:bg-gray-100 disabled:cursor-not-allowed"
            >
              <option value="CREW">Crew Member</option>
              <option value="ADMIN">Admin</option>
              <option value="OWNER">Owner</option>
            </select>
            <p className="mt-1 text-xs text-gray-500">
              {isSelf
                ? 'You cannot change your own role. Another owner has to do it.'
                : 'Admins can manage events and crew. Owners have full organization access.'}
            </p>
          </div>

          {/* No Status field.
              The old one wrote User.status, which is PLATFORM-WIDE: setting it
              INACTIVE locked the person out of every organization they crewed
              for, not just this one. That is not an org admin's call to make
              about a freelancer they share. The two per-org states — archive
              and deactivate — live on the crew profile page instead, where
              there is room to say what each one actually does. */}
        </div>

        {/* Pay Rates (only when editing and payment tracking enabled) */}
        {showRates && (
          <div className="border-t border-gray-200 pt-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Pay Rates</h3>
            <p className="text-sm text-gray-500 mb-4">
              Set default pay rates for this crew member. These can be overridden on specific event roles.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Hourly Rate */}
              <div>
                <label htmlFor="defaultHourlyRate" className="block text-sm font-medium text-gray-700 mb-1">
                  Hourly Rate
                </label>
                <div className="relative">
                  <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-500">$</span>
                  <input
                    type="number"
                    id="defaultHourlyRate"
                    step="0.01"
                    min="0"
                    {...register('defaultHourlyRate')}
                    className="block w-full pl-7 pr-12 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-rack focus:border-rack"
                    placeholder="0.00"
                  />
                  <span className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500">/hr</span>
                </div>
              </div>

              {/* Daily Rate */}
              <div>
                <label htmlFor="defaultDailyRate" className="block text-sm font-medium text-gray-700 mb-1">
                  Daily/Flat Rate
                </label>
                <div className="relative">
                  <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-500">$</span>
                  <input
                    type="number"
                    id="defaultDailyRate"
                    step="0.01"
                    min="0"
                    {...register('defaultDailyRate')}
                    className="block w-full pl-7 pr-12 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-rack focus:border-rack"
                    placeholder="0.00"
                  />
                  <span className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500">/day</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Info note for new users */}
        {!isEditing && (
          <div className="bg-surface-panel border border-hairline border-l-[3px] border-l-rack p-4">
            <div className="flex">
              <svg className="h-5 w-5 text-blue-400 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-sm text-blue-700">
                A random password will be generated for the new user. You'll be able to see and copy it after creation.
              </p>
            </div>
          </div>
        )}

        {/* Form Actions */}
        <div className="flex items-center justify-end gap-4 pt-6 border-t border-gray-200">
          <Button
            type="button"
            variant="secondary"
            onClick={() => navigate('/admin/crew')}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            size="lg"
            loading={isSubmitting}
            loadingText={isEditing ? 'Saving...' : 'Sending Invite...'}
          >
            {isEditing ? 'Save Changes' : 'Send Invite'}
          </Button>
        </div>
      </form>
    </div>
  );
}
